import {useState, useEffect} from 'react';
import {useAuth} from '@/context/use-auth';
import {useMembersTranslation} from '@/i18n/hooks';
import {Heading} from '@/components/tailwind/heading';
import {useToast} from '@/hooks/use-toast';
import {
    getSpecialAssignmentServices,
    createSpecialAssignmentBooking,
    SpecialAssignmentService,
    CreateSpecialAssignmentBookingRequest,
} from '@/services/special-assignment-services';
import SpecialAssignmentBookingTab from './special-assignment-booking-tab';
import SpecialAssignmentHistoryTab from './special-assignment-history-tab';

type TabKey = 'booking' | 'history';

export default function SpecialAssignmentsPage() {
    const {t} = useMembersTranslation();
    const {token} = useAuth();
    const {toast} = useToast();

    const [activeTab, setActiveTab] = useState<TabKey>('booking');
    const [services, setServices] = useState<SpecialAssignmentService[]>([]);
    const [historyRefreshTrigger, setHistoryRefreshTrigger] = useState(0);

    useEffect(() => {
        if (!token) return;

        getSpecialAssignmentServices(token)
            .then(setServices)
            .catch(() => {});
    }, [token]);

    async function handleConfirm(request: CreateSpecialAssignmentBookingRequest) {
        await createSpecialAssignmentBooking(token, request);
        toast({
            title: t('specialAssignments.toast.success.title'),
            description: t('specialAssignments.toast.success.message', {count: request.memberIds.length}),
        });
    }

    function handleBookingCreated() {
        setHistoryRefreshTrigger(n => n + 1);
    }

    const tabClasses = (tab: TabKey) =>
        `px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === tab
                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
        }`;

    return (
        <div className="space-y-6">
            <Heading>{t('specialAssignments.pageTitle')}</Heading>

            <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex gap-2">
                    <button
                        className={tabClasses('booking')}
                        onClick={() => setActiveTab('booking')}
                        data-testid="tab-booking"
                    >
                        {t('specialAssignments.tabs.booking')}
                    </button>
                    <button
                        className={tabClasses('history')}
                        onClick={() => setActiveTab('history')}
                        data-testid="tab-history"
                    >
                        {t('specialAssignments.tabs.history')}
                    </button>
                </nav>
            </div>

            {activeTab === 'booking' && (
                <SpecialAssignmentBookingTab
                    services={services}
                    onBookingCreated={handleBookingCreated}
                    onConfirm={handleConfirm}
                />
            )}

            {activeTab === 'history' && (
                <SpecialAssignmentHistoryTab
                    refreshTrigger={historyRefreshTrigger}
                    services={services}
                    onBookingUpdated={handleBookingCreated}
                />
            )}
        </div>
    );
}
